import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  input,
  Input,
  linkedSignal,
  signal,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  ViewChild,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ViewportScroller } from '@angular/common';

import {
  BehaviorSubject,
  catchError,
  combineLatest,
  delay,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  Subject,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  ButtonPosition,
  ButtonSize,
  ButtonTheme,
  IconButtonSize,
  IconButtonTheme,
  IconPosition,
  LoaderTheme,
  ModalButtons,
  NotificationTheme,
  TagTheme,
} from '@fcom/ui-components';
import {
  Category,
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairOperatingAirlineId,
  FinnairOrder,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairServiceCatalogEligibilityReason,
  FinnairServiceItem,
  FinnairServiceVariantItemV3,
  SeatInfo,
  SeatmapSvgAndControlData,
  SubCategory,
} from '@fcom/dapi/api/models';
import { isFinnairNorraOrWetLease, isPresent, resolveTailName, stopPropagation, unsubscribe } from '@fcom/core/utils';
import { ElementActions, ElementTypes, GtmPurchaseFlow } from '@fcom/common/interfaces';
import { ServiceCatalogSelectionsForAllTravelers } from '@fcom/dapi/interfaces';
import { AIRCRAFT_TRANSLATION_MAP, SeatMapStatus } from '@fcom/common/interfaces/seat-map.interface';
import { MonitoringLogger } from '@fcom/core/services';
import { CmsArticle, CmsTemplate } from '@fcom/core-api';
import { GtmService } from '@fcom/common/gtm';
import { finShare, snapshot } from '@fcom/rx';
import { FinnairThemeService, FragmentService, ScrollService } from '@fcom/common/services';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { asTravelerService } from '../../../../../store/utils/services.utils';
import {
  seatBundleCatalogContainsMatchSelector,
  passengerCanAddBundleSelector,
  passengerHasSpecificBundleSelector,
  selectedBundleCategoriesByPassengerSelector,
  seatBundleSelectionsByFlightAndPassengerSelector,
  bundleServiceMatchesForSeatSelector,
  bundleQuotaReachedForFlightAndCategorySelector,
} from '../../../../../store/selectors/bundle.selector';
import { selectedSeatNumberByFlightAndPassengerSelector } from '../seat-map-components.selector';
import {
  originalRegularSeatByFlightSelector,
  seatMapForFlight,
  seatMapWithStatus,
  seatSelectionsForFlight,
} from '../../../../../store/selectors/seat-map.selector';
import {
  EligibilitiesForFragments,
  Notification,
  ServiceOverlapResolution,
  SeatChangeEvent,
  SeatDetails,
  SeatMapTraveler,
  ServiceOverlaps,
} from '../../../interfaces';
import { SeatMapRendererComponent } from '../seat-map-renderer/seat-map-renderer.component';
import { SeatPassengersComponent } from '../seat-passengers/seat-passengers.component';
import {
  getSeatMapFlight,
  getSeatType,
  getServiceOverlapStateForPaxForFlight,
  getServicesNotifications,
  isAllowedToChangeSeat,
  servicesForFlightAndTravelerId,
} from '../../../utils';
import { AncillaryLoaderStatus } from '../../../enums';
import { CommonBookingFeatureState, SeatMapActions, seatSelections, ServicesActions } from '../../../../../store';
import { isOrder } from '../../../../../utils/common-booking.utils';
import { isFlight } from '../../../../../utils/flight.utils';
import { SeatBenefitTag } from '../seat-benefit/seat-benefit.component';
import { BENEFITS } from '../../../../../utils/benefit.utils';
import { overlappingSelectionsForCategorySelector } from '../../../../../store/selectors/services.selector';

const FLIGHT_CONTAINER_ANIMATION_DELAY = 350;

interface OperatingAirlineIdInfo {
  operatingAirlineId: FinnairOperatingAirlineId;
  isOperated: boolean;
  isOrderIdIdentical: boolean;
  isOrder: boolean;
  travelExtrasPurchaseNotEligible: boolean;
}

@Component({
  selector: 'fin-seat-map-view',
  styleUrls: ['./seat-map-view.component.scss'],
  templateUrl: './seat-map-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SeatMapViewComponent implements OnInit, OnDestroy {
  private fragmentService = inject(FragmentService);
  private viewportScroller = inject(ViewportScroller);
  private gtmService = inject(GtmService);
  private monitoringLogger = inject(MonitoringLogger);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private store$ = inject<Store<CommonBookingFeatureState>>(Store);
  private bundleSeatData = this.store$.selectSignal(bundleServiceMatchesForSeatSelector);
  private scrollService = inject(ScrollService);
  private injector = inject(Injector);
  private themeService = inject(FinnairThemeService);

  isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  readonly ButtonPosition = ButtonPosition;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly Category = Category;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconPosition = IconPosition;
  readonly LoaderTheme = LoaderTheme;
  readonly ModalButtons = ModalButtons;
  readonly SeatMapStatus = SeatMapStatus;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly NotificationTheme = NotificationTheme;
  readonly IconLibrary = IconLibrary;

  flight = input.required<FinnairItineraryItemFlight>();
  passengers = input.required<FinnairPassengerItem[]>();
  contextCategory = input.required<Category>();

  @Input() flightIsActive$: Observable<boolean>;
  @Input() cartOrOrder$: Observable<FinnairCart | FinnairOrder>;
  @Input() includedSeats: FinnairServiceItem | null;
  @Input() availability$: Observable<EligibilitiesForFragments> = of({});
  @Input() canClearSeats = true;
  @Input() notEligibleWhenCheckInClosed = false;
  @Input() preSelectedPax$: Observable<string> = of(undefined);
  @Input() ancillaryLoader$: BehaviorSubject<AncillaryLoaderStatus>;
  @Input() skipScroll = false;
  @Input({ required: true }) purchaseFlow: GtmPurchaseFlow;

  @Output() changeSeats: EventEmitter<SeatChangeEvent> = new EventEmitter();
  @Output() retrySeatmap: EventEmitter<FinnairItineraryItemFlight> = new EventEmitter();
  @Output() isReassigningSeats: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('seatMap', { read: ElementRef }) seatMapRef: ElementRef;
  @ViewChild('comboToggle', { read: ElementRef }) comboToggleRef: ElementRef;
  seatMapElement = viewChild<SeatMapRendererComponent>('seatMap');
  seatPassengers = viewChild(SeatPassengersComponent);

  subscriptions: Subscription = new Subscription();
  selectablePassengers$: BehaviorSubject<SeatMapTraveler[]> = new BehaviorSubject([]);
  seatDetails$: Subject<SeatDetails> = new ReplaySubject<SeatDetails>(1);
  seatMapContainerWidth$: BehaviorSubject<number | undefined> = new BehaviorSubject(undefined);
  unmodifiableSeat$: Observable<boolean>;
  unmodifiableFreeSeat$: Observable<boolean>;
  noSeatmapNotifications$: Observable<Notification[]>;
  currentFlightTail: string;
  exitRowModalOpen = false;
  aircraftModalOpen = false;
  aircraftInformationTemplate$: BehaviorSubject<CmsTemplate | undefined> = new BehaviorSubject(undefined);
  isFinnairWetLease = false;
  operatingAirlineIdInfo$: Observable<OperatingAirlineIdInfo>;
  benefitTextKey: string;
  benefitTag: SeatBenefitTag;

  passengersIncludeInfants = computed(() =>
    this.passengers().some((p) => p.passengerTypeCode === FinnairPassengerCode.INF)
  );
  selectedSeats$: Observable<string[]>;
  selectedSeatsByPax$: Observable<Record<string, string>>;

  activePax$: Observable<SeatMapTraveler | undefined>;

  // Seat details to warn about when the active pax's complimentary seat is incompatible with the bundle; null otherwise.
  incompatibleComboSeat$: Observable<{ seatTypeKey: string; seatNumber: string } | null>;
  selectedBundleCategoriesByPax$: Observable<Record<string, Category[]>>;
  activePaxHasBundleForCurrentCategory$: Observable<boolean>;
  activePaxCanAddBundle$: Observable<boolean>;
  bundleModifyingEnabled$: Observable<boolean>;
  bundleNotAvailableForFlightForPax$: Observable<boolean>;
  displayBundleChips$: Observable<Category | undefined>;
  bundleQuotaReached$: Observable<boolean>;
  selectionBlockedByQuota$: Observable<boolean>;
  seatSelectionDisabledForBundle$: Observable<boolean>;
  bundleSeatRemovalConfirmModalOpen = false;
  activePaxHadRegularSeat$: Observable<boolean>;
  seatMapStatus$: Observable<SeatMapStatus>;
  // Re-derived when the selectable passengers change: the current pick survives as long as they are still
  // in the list, so an order refresh cannot silently move the passenger out from under the user.
  activePaxId: WritableSignal<string> = linkedSignal<string[], string>({
    source: () => this.selectablePassengerIds(),
    computation: (passengerIds, previous) =>
      previous && passengerIds.includes(previous.value) ? previous.value : (passengerIds[0] ?? ''),
  });
  readonly activePaxId$: Observable<string> = toObservable(this.activePaxId, { injector: this.injector });
  private readonly bundleInCatalogSelectorForActivePax = computed(() =>
    seatBundleCatalogContainsMatchSelector({
      flightId: this.flight().id,
      travelerId: this.activePaxId(),
      category: this.contextCategory(),
    })
  );
  readonly displayBundlePromotionCard = computed(() =>
    this.store$.selectSignal(this.bundleInCatalogSelectorForActivePax())()
  );

  private readonly serviceOverlapsForCategory = computed(() =>
    this.store$.selectSignal(overlappingSelectionsForCategorySelector(this.contextCategory()))()
  );

  readonly serviceOverlapsForActivePax: Signal<ServiceOverlaps> = computed(() => {
    const resolution = this.overlapResolutions()[this.activePaxId()];
    const overlaps = this.serviceOverlapsForCategory() ?? {};

    return getServiceOverlapStateForPaxForFlight(overlaps, this.flight().id, this.activePaxId(), resolution);
  });

  // Filtered to be present, mirroring the previous flight.seatMap$ input (which never emitted before load).
  private seatMap$: Observable<SeatmapSvgAndControlData>;
  private overlapResolutions: WritableSignal<Record<string, ServiceOverlapResolution>> = signal({});

  ngOnInit(): void {
    this.currentFlightTail = resolveTailName(this.flight().operatingAirline.code);

    this.seatMap$ = this.store$.select(seatMapForFlight(this.flight().id)).pipe(filter(isPresent));
    this.seatMapStatus$ = this.store$.select(seatMapWithStatus(this.flight().id)).pipe(
      map((seatMapWithStatusValue) => seatMapWithStatusValue?.status),
      distinctUntilChanged()
    );

    this.selectedSeatsByPax$ = this.store$
      .select(selectedSeatNumberByFlightAndPassengerSelector)
      .pipe(map((i) => i[this.flight().id] || {}));

    this.selectedSeats$ = this.selectedSeatsByPax$.pipe(
      map((seatsByPax) => {
        return seatsByPax ? Object.values(seatsByPax).filter(Boolean) : [];
      })
    );

    this.subscriptions.add(
      combineLatest([
        toObservable(this.passengers, { injector: this.injector }),
        this.store$.select(seatSelections),
        this.seatMap$,
      ]).subscribe(([passengers, currentSeatSelections, seatMap]) => {
        this.selectablePassengers$.next(this.mapPassengers(passengers, currentSeatSelections, seatMap));
      })
    );

    this.subscriptions.add(
      this.preSelectedPax$.pipe(filter(Boolean)).subscribe((preSelectedPax) => {
        if (this.selectablePassengerIds().includes(preSelectedPax)) {
          this.activePaxId.set(preSelectedPax);
        }
      })
    );

    this.activePax$ = combineLatest([this.selectablePassengers$, this.activePaxId$]).pipe(
      map(([passengers, activePaxId]) => passengers.find((p) => p.id === activePaxId)),
      filter(Boolean),
      distinctUntilChanged(),
      finShare()
    );

    this.activePaxHasBundleForCurrentCategory$ = this.activePaxId$.pipe(
      switchMap((paxId) =>
        this.store$.select(
          passengerHasSpecificBundleSelector({
            travelerId: paxId,
            flightId: this.flight().id,
            category: this.contextCategory(),
          })
        )
      )
    );

    this.selectedBundleCategoriesByPax$ = this.store$.select(
      selectedBundleCategoriesByPassengerSelector({
        flightId: this.flight().id,
      })
    );

    this.bundleNotAvailableForFlightForPax$ = this.activePaxId$.pipe(
      switchMap((paxId) =>
        this.store$
          .select(
            seatBundleCatalogContainsMatchSelector({
              travelerId: paxId,
              flightId: this.flight().id,
              category: this.contextCategory(),
            })
          )
          .pipe(map((inCatalog) => this.contextCategory() !== Category.SEAT && !inCatalog))
      )
    );

    this.activePaxCanAddBundle$ = this.activePaxId$.pipe(
      switchMap((paxId) =>
        this.store$.select(
          passengerCanAddBundleSelector({
            travelerId: paxId,
            flightId: this.flight().id,
            category: this.contextCategory(),
          })
        )
      )
    );

    this.bundleQuotaReached$ = this.store$.select(
      bundleQuotaReachedForFlightAndCategorySelector({
        flightId: this.flight().id,
        category: this.contextCategory(),
      })
    );

    this.bundleModifyingEnabled$ = combineLatest([
      this.activePaxCanAddBundle$,
      this.activePaxHasBundleForCurrentCategory$,
      this.bundleQuotaReached$,
      this.activePaxId$.pipe(
        switchMap((activePaxId) =>
          this.store$.select(
            seatBundleCatalogContainsMatchSelector({
              travelerId: activePaxId,
              flightId: this.flight().id,
              category: this.contextCategory(),
            })
          )
        )
      ),
    ]).pipe(
      map(
        ([canAddBundle, canRemoveBundle, quotaReached, catalogContainsBundle]) =>
          !!canAddBundle || !!canRemoveBundle || (quotaReached && catalogContainsBundle)
      )
    );

    this.displayBundleChips$ = this.bundleModifyingEnabled$.pipe(
      map((modifiable) => (modifiable ? this.contextCategory() : undefined))
    );

    this.selectionBlockedByQuota$ = combineLatest([
      this.bundleQuotaReached$,
      this.activePaxHasBundleForCurrentCategory$,
    ]).pipe(map(([quotaReached, holdsThisCombo]) => quotaReached && !holdsThisCombo));

    this.seatSelectionDisabledForBundle$ = combineLatest([
      this.selectionBlockedByQuota$,
      this.bundleNotAvailableForFlightForPax$,
      toObservable(this.serviceOverlapsForActivePax, { injector: this.injector }),
    ]).pipe(
      map(
        ([blockedByQuota, blockedByAvailability, serviceOverlaps]) =>
          blockedByAvailability || blockedByQuota || serviceOverlaps.blockSelections
      )
    );

    this.activePaxHadRegularSeat$ = combineLatest([
      this.activePaxId$,
      this.store$.select(originalRegularSeatByFlightSelector),
    ]).pipe(
      map(
        ([activePaxId, originalRegularSeatByFlight]) => !!originalRegularSeatByFlight[this.flight().id]?.[activePaxId]
      )
    );

    this.incompatibleComboSeat$ = combineLatest([
      this.activePaxId$,
      this.selectedSeatsByPax$,
      this.seatMap$,
      this.activePaxHadRegularSeat$,
    ]).pipe(
      map(([activePaxId, selectedSeatsByPax, seatMap, hadRegularSeat]) => {
        const seatNumber = activePaxId ? selectedSeatsByPax?.[activePaxId] : undefined;
        if (!activePaxId || !hadRegularSeat || !seatNumber) {
          return null;
        }
        const seatCompatibleWithBundle = !!(
          this.bundleSeatData()[this.flight().id] as Record<
            string,
            Record<string, Record<string, FinnairServiceVariantItemV3>>
          >
        )?.[activePaxId]?.[this.contextCategory()]?.[seatNumber];
        if (seatCompatibleWithBundle) {
          return null;
        }
        const seat = seatMap?.seats?.find((s) => s.seatNumber === seatNumber);
        return { seatTypeKey: getSeatType(seat?.characteristics).label, seatNumber };
      })
    );

    this.subscriptions.add(
      this.seatMap$.subscribe((seatMap) => {
        this.isFinnairWetLease = seatMap.isWetlease;

        // Wait until seat map element is visible before triggering seat chips to render.
        setTimeout(() => {
          const seatMapEl = this.seatMapElement()?.seatMapWrapper?.nativeElement;
          this.seatMapContainerWidth$.next(seatMapEl?.clientWidth);
          this.changeDetectorRef.detectChanges();
        }, 0);
      })
    );

    this.subscriptions.add(
      combineLatest([
        this.selectablePassengers$,
        this.flightIsActive$.pipe(delay(FLIGHT_CONTAINER_ANIMATION_DELAY)), // Delay to prevent transition collision, to be refactored
        this.seatMapStatus$,
        this.ancillaryLoader$,
        this.cartOrOrder$,
      ])
        .pipe(
          filter(
            ([_pax, active, seatMapStatus, _loaderStatus, _order]) => active && seatMapStatus === SeatMapStatus.OK
          ),
          take(1),
          map(([pax, _active, _seatMapStatus, loaderStatus, order]) => {
            return { pax, loaderStatus, order };
          })
        )
        .subscribe(({ pax, loaderStatus, order }) => {
          if (loaderStatus === AncillaryLoaderStatus.REASSIGNING) {
            this.store$.dispatch(SeatMapActions.resetSeatSelections({ cartOrOrder: order! }));
          }
          if (pax.length > 0 && !this.skipScroll) {
            this.tryScrollToFirstAvailableSeat(pax[0]);
          }
        })
    );

    this.subscriptions.add(
      this.cartOrOrder$.subscribe((cartOrOrder) => {
        this.setBenefitDetails(this.flight(), cartOrOrder);
      })
    );

    this.subscriptions.add(
      this.seatMapStatus$
        .pipe(
          filter(
            (status: SeatMapStatus) =>
              status === SeatMapStatus.OK ||
              status === SeatMapStatus.LOAD_ERROR ||
              status === SeatMapStatus.OK_NO_AVAILABILITY
          )
        )
        .subscribe(() => {
          this.ancillaryLoader$.next(AncillaryLoaderStatus.OFF);
          this.isReassigningSeats.emit(false);
        })
    );

    const activePaxSeatSelection$ = combineLatest([
      this.store$.select(seatSelectionsForFlight(this.flight().id)),
      this.activePaxId$,
    ]).pipe(
      map(([selections, activePaxId]) => selections?.[activePaxId]),
      finShare()
    );

    this.unmodifiableSeat$ = activePaxSeatSelection$.pipe(map((selection) => selection?.paid && !selection.modifiable));
    this.unmodifiableFreeSeat$ = activePaxSeatSelection$.pipe(
      map((selection) => !!selection?.seatNumber && !selection.paid && selection.ticketed && !selection.modifiable)
    );

    this.operatingAirlineIdInfo$ = combineLatest([this.cartOrOrder$, this.availability$]).pipe(
      map(([cartOrOrder, availability]) => {
        const isOrderCreated = isOrder(cartOrOrder);

        const operatingAirlineId = isOrderCreated
          ? this.getOperatingAirlineId(cartOrOrder as FinnairOrder, this.flight().operatingAirline.code)
          : undefined;

        return {
          operatingAirlineId,
          travelExtrasPurchaseNotEligible: (availability[this.flight().id] ?? []).some(
            (eligibility) =>
              eligibility.reason === FinnairServiceCatalogEligibilityReason.PURCHASE_NOT_ELIGIBLE_FOR_TRAIN ||
              eligibility.reason === FinnairServiceCatalogEligibilityReason.PURCHASE_NOT_ELIGIBLE_FOR_BUS
          ),
          isOperated: isOrderCreated ? !!operatingAirlineId : this.flight().operatingAirline.code !== 'AY',
          isOrderIdIdentical: !!operatingAirlineId && cartOrOrder.id === operatingAirlineId.id,
          isOrder: isOrderCreated,
        };
      }),
      finShare()
    );
    this.noSeatmapNotifications$ = combineLatest([this.availability$, this.operatingAirlineIdInfo$]).pipe(
      map(([availability, operatingAirlineIdInfo]) =>
        getServicesNotifications(
          {},
          availability,
          this.contextCategory(),
          this.flight(),
          false,
          operatingAirlineIdInfo.isOperated
        )
      ),
      finShare()
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  tryScrollToFirstAvailableSeat(
    pax: SeatMapTraveler,
    attempt = 0,
    shouldFocus = false,
    preferredSeatNumber?: string
  ): void {
    if (attempt >= 10) {
      this.monitoringLogger.error('Failed to find the seatmap after 1000ms', {
        flightNumber: this.flight().flightNumber,
        cabinClass: this.flight().cabinClass,
        id: snapshot(this.cartOrOrder$).id,
      });

      return;
    }

    setTimeout(() => {
      const seatMap = this.seatMapElement()?.seatMap?.nativeElement;

      if (!seatMap) {
        const nextAttempt = attempt + 1;
        this.tryScrollToFirstAvailableSeat(pax, nextAttempt, shouldFocus, preferredSeatNumber);
        return;
      }

      const seatNumber =
        preferredSeatNumber ??
        pax.seat?.seatNumber ??
        seatMap?.querySelector(`[data-${pax.id.toLowerCase()}="available"]`)?.getAttribute('data-id');

      if (seatNumber) {
        this.scrollToSeat(seatNumber, shouldFocus);
      }
    }, 100);
  }

  selectSeat({
    passengerId,
    seatNumber,
    exitRow,
  }: {
    passengerId: string;
    seatNumber: string;
    exitRow: boolean;
  }): void {
    const isModifiableSeats: boolean =
      !this.includedSeats || isAllowedToChangeSeat(this.includedSeats, this.flight().id, passengerId);
    if (!isModifiableSeats) {
      return;
    }

    this.subscriptions.add(
      this.seatMap$.pipe(take(1)).subscribe((seatMap) => {
        // When clearing is not allowed (check-in) a passenger must always keep a seat: deselecting their
        // current seat reverts to the system-assigned seat instead of leaving them with none. The renderer
        // emits an undefined seatNumber on deselection, so guard against both null and undefined.
        const shouldRevertSeat = !this.canClearSeats && !isPresent(seatNumber);
        const revertSeatNumber = servicesForFlightAndTravelerId(this.includedSeats, this.flight().id, passengerId)[0]
          ?.seatNumber;
        if (shouldRevertSeat && !isPresent(revertSeatNumber)) {
          // Nothing to revert to: keep the current seat rather than leaving the passenger seatless.
          return;
        }
        const actualSeatNumber = shouldRevertSeat ? revertSeatNumber : seatNumber;
        const seat: SeatInfo = seatMap.seats.find((s) => s.seatNumber === actualSeatNumber);
        if (seat && exitRow && actualSeatNumber) {
          this.seatDetails$.next({
            travelerId: passengerId,
            seat,
            seatNumber: actualSeatNumber,
            seatType: seat.type,
            exitRow,
          });
          this.exitRowModalOpen = true;
          this.gtmService.seatMapEvent('exit-row-modal', ElementTypes.MODAL, '', ElementActions.VIEW, {
            flightInfo: getSeatMapFlight(this.flight()),
          });
        } else {
          this.confirmSeatSelection(passengerId, actualSeatNumber);
        }
      })
    );
  }

  confirmExitRowSeat(passengerId: string, seatNumber: string): void {
    this.confirmSeatSelection(passengerId, seatNumber);
    this.gtmService.seatMapEvent('exit-row-confirm', ElementTypes.BUTTON, 'cta', ElementActions.CLICK, {
      flightInfo: getSeatMapFlight(this.flight()),
    });
  }

  confirmSeatSelection(passengerId: string, seatNumber: string): void {
    this.exitRowModalOpen = false;
    this.subscriptions.add(
      this.selectablePassengers$.pipe(take(1)).subscribe((currentPassengers) => {
        const bundleMatch: FinnairServiceVariantItemV3 = (
          this.bundleSeatData()[this.flight().id] as Record<
            string,
            Record<string, Record<string, FinnairServiceVariantItemV3>>
          >
        )?.[this.activePaxId()]?.[this.contextCategory()]?.[seatNumber];

        this.changeSeats.emit({
          travelerId: passengerId,
          seatNumber,
          flightId: this.flight().id,
          bundledService: bundleMatch ? { ...asTravelerService(bundleMatch), seatNumber } : undefined,
        });

        this.selectablePassengers$.next(
          currentPassengers.map((pax) => {
            return {
              ...pax,
              ...(pax.id === passengerId && {
                seatNumber,
              }),
            };
          })
        );
      })
    );
  }

  selectPassenger(passengerId: string): void {
    this.activePaxId.set(passengerId);

    const selectedPax = this.selectablePassengers$.getValue().find((pax) => pax.id === passengerId);
    if (selectedPax?.seatNumber) {
      this.scrollToSeat(selectedPax.seatNumber, true);
    } else {
      this.tryScrollToFirstAvailableSeat(selectedPax, 0, true);
    }
  }

  onBundleToggle(): void {
    this.subscriptions.add(
      combineLatest([
        this.activePax$,
        this.activePaxHasBundleForCurrentCategory$,
        this.activePaxCanAddBundle$,
        this.store$.select(originalRegularSeatByFlightSelector),
      ])
        .pipe(take(1))
        .subscribe(([activePax, previousToggledValue, canAddBundle, originalRegularSeatByFlight]) => {
          const matchingBundleService = (
            this.bundleSeatData()[this.flight().id] as Record<
              string,
              Record<string, Record<string, FinnairServiceVariantItemV3>>
            >
          )?.[activePax?.id ?? '']?.[this.contextCategory()]?.[activePax?.seatNumber ?? ''];

          if (previousToggledValue) {
            if (originalRegularSeatByFlight[this.flight().id]?.[activePax.id]) {
              this.removeBundleAncillaryAndRestoreSeatSelection();
            } else {
              this.store$.dispatch(
                ServicesActions.setSelectionForFragmentForTraveler({
                  category: this.contextCategory(),
                  fragmentId: this.flight().id,
                  travelerId: activePax.id,
                  selection: undefined,
                })
              );
            }
            return;
          }

          if (!previousToggledValue && activePax.seatNumber && !matchingBundleService) {
            this.bundleSeatRemovalConfirmModalOpen = true;
            return;
          }

          if (activePax.seatNumber) {
            this.changeSeats.emit({
              travelerId: activePax.id,
              seatNumber: activePax.seatNumber,
              flightId: this.flight().id,
              bundledService:
                !previousToggledValue && canAddBundle && matchingBundleService
                  ? { ...asTravelerService(matchingBundleService), seatNumber: activePax.seatNumber }
                  : undefined,
            });

            if (!previousToggledValue) {
              this.store$.dispatch(
                ServicesActions.setSelectionForFragmentForTraveler({
                  category: this.contextCategory(),
                  fragmentId: this.flight().id,
                  travelerId: activePax.id,
                  selection: {
                    ...asTravelerService(matchingBundleService),
                    seatNumber: activePax.seatNumber,
                  },
                })
              );
            }
          }

          if (
            !previousToggledValue &&
            !this.scrollService.isInViewPort(this.seatMapRef.nativeElement, { visibility: 'top', offsetY: 100 })
          ) {
            this.scrollService.smoothScroll(this.comboToggleRef.nativeElement, {
              edgeY: 'center',
              offsetY: -100,
            });
          }
        })
    );
  }

  /**
   * Called when the user removes a bundle that was added as a seat upsell.
   * Reads the seat number stored in the bundle selection and dispatches
   * `SeatMapActions.setSeatForFlight`, which atomically restores the original seat in
   * `seatMap.seatmaps[flightId].selections` and clears all seat-bundle category selections via the
   * services reducer (`on(SeatMapActions.setSeatForFlight, ...)`).
   */
  private removeBundleAncillaryAndRestoreSeatSelection(): void {
    this.subscriptions.add(
      combineLatest([this.store$.select(seatBundleSelectionsByFlightAndPassengerSelector), this.seatMap$])
        .pipe(take(1))
        .subscribe(([bundleSelections, seatMap]) => {
          const activePaxId = this.activePaxId();
          const bundleSeatNumber = bundleSelections[this.flight().id]?.[activePaxId]?.find(
            (s) => s.seatNumber
          )?.seatNumber;
          const seat = seatMap.seats.find((s) => s.seatNumber === bundleSeatNumber);
          this.store$.dispatch(
            SeatMapActions.setSeatForFlight({
              flightId: this.flight().id,
              travelerId: activePaxId,
              seat,
            })
          );
        })
    );
  }

  confirmSeatRemovalAndBundleActivation(): void {
    const activePaxId = this.activePaxId();

    this.changeSeats.emit({
      travelerId: activePaxId,
      seatNumber: undefined,
      flightId: this.flight().id,
    });

    this.selectablePassengers$.next(
      this.selectablePassengers$
        .getValue()
        .map((pax) => (pax.id === activePaxId ? { ...pax, seatNumber: undefined, seat: undefined } : pax))
    );

    this.bundleSeatRemovalConfirmModalOpen = false;
  }

  scrollToSeat(seatNumber: string, focus = true): void {
    requestAnimationFrame(() => {
      const seatMap: HTMLDivElement = this.seatMapElement()?.seatMapWrapper?.nativeElement;

      if (!seatMap) {
        return;
      }

      const seatElement: HTMLAnchorElement = seatMap.querySelector(`[data-id="${seatNumber}"] a`);

      if (!seatElement) {
        return;
      }

      /**
       * Calculate if the seat can be centered or not. If this is not done this can cause bugs
       * where the slider partially moves onto the next seatmap as the browser tries to force the
       * seat to the center of the view.
       */
      let isCenterable = false;

      try {
        let seatGElement = seatElement.parentElement as unknown as SVGGElement;

        /**
         * Business seats are wrapped in 2 levels of parent SVGGElements. So we need to go to the
         * parents parent to get the actual positioning. (The `transform: translate(100, 200)` that
         * you see) in the elements panel on dev tools.
         */
        if (!seatGElement.transform.baseVal.length) {
          seatGElement = seatGElement.parentElement as unknown as SVGGElement;
        }

        const halfViewArea = seatMap.getBoundingClientRect().width * 0.5;
        const svgWidth = seatMap.querySelector('svg').getBoundingClientRect().width;
        const seatXCoordinate = seatGElement.transform.baseVal.consolidate().matrix.e;

        isCenterable = svgWidth - seatXCoordinate > halfViewArea && seatXCoordinate > halfViewArea;
      } catch (e) {
        const cartOrOrderId = snapshot(this.cartOrOrder$).id;
        this.monitoringLogger.error('Failed to calculate seatmap scroll to seat', {
          message: e.message,
          stack: e.stack.substr(0, 500),
          flightNumber: this.flight().flightNumber,
          cabinClass: this.flight().cabinClass,
          // A quick filter for PNR's, so that only cart ids are reported to Sentry.
          id: cartOrOrderId.length === 6 ? null : cartOrOrderId,
        });
      }

      seatElement.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: isCenterable ? 'center' : 'nearest',
      });

      if (focus) {
        seatElement.focus({ preventScroll: true });
      }
    });
  }

  moveFocus(e: Event, el: string): void {
    stopPropagation(e);
    this.viewportScroller.scrollToAnchor(el);
  }

  openAirplaneModal(): void {
    this.aircraftModalOpen = true;
    const fragmentKey = `fragments.airCraftInfo.${this.getAircraftTranslationKey()}.url`;
    this.subscriptions.add(
      this.fragmentService
        .getFragment<CmsArticle>(fragmentKey)
        .pipe(
          filter(Boolean),
          map((fragment) => ({ header: [], main: [fragment], footer: [] })),
          catchError((error: unknown) => {
            this.monitoringLogger.error('Failed to load aircraft information fragment', { fragmentKey, error });
            return of({ header: [], main: [], footer: [] });
          })
        )
        .subscribe((template) => this.aircraftInformationTemplate$.next(template))
    );
    this.gtmService.seatMapEvent('aircraft-info-modal', ElementTypes.MODAL, '', ElementActions.VIEW, {
      flightInfo: getSeatMapFlight(this.flight()),
    });
  }

  setBenefitDetails(flight: FinnairItineraryItemFlight, order: FinnairOrder | FinnairCart): void {
    const bound = order.bounds.find((bound) =>
      bound.itinerary.some((itinerary: FinnairItineraryItemFlight) => itinerary.id === flight.id)
    );
    const hasFareFamilySeatBenefit = order.benefits?.[bound.fareFamily.code]?.translatedBenefitRows.find(
      (benefit) => benefit.key === BENEFITS.seatSelection
    )?.isPositive;
    const hasCorporateSeatBenefit =
      !hasFareFamilySeatBenefit && order.otherInformation?.corporateUserInformation?.isEligibleForSeatBenefit;
    if (hasCorporateSeatBenefit) {
      this.benefitTextKey = 'seatMap.benefits.corporateBenefitDescription';
      this.benefitTag = {
        textKey: 'seatMap.benefits.corporateBenefitTag',
        theme: TagTheme.DEFAULT,
        icon: {
          name: SvgLibraryIcon.B2B,
        },
      };
    }
  }

  closeSeatRemovalBundleModal(): void {
    this.bundleSeatRemovalConfirmModalOpen = false;
  }

  dismissOverlapNotificationForPax(paxId: string, preExistingSeatNumber?: string | undefined): void {
    this.seatPassengers()?.focusPassenger(paxId);
    this.overlapResolutions.update((resolutions) => ({ ...resolutions, [paxId]: 'discard' }));

    if (preExistingSeatNumber) {
      this.confirmSeatSelection(paxId, preExistingSeatNumber);
    }
  }

  keepExistingSelectionForPax(paxId: string): void {
    this.seatPassengers()?.focusPassenger(paxId);
    this.overlapResolutions.update((resolutions) => ({ ...resolutions, [paxId]: 'keep' }));
  }

  private getAircraftTranslationKey(): string | undefined {
    const code = this.flight().aircraft.code;
    if (!AIRCRAFT_TRANSLATION_MAP[code]) {
      this.monitoringLogger.error('Invalid aircraft model', { aircraft: this.flight().aircraft });
    }

    return AIRCRAFT_TRANSLATION_MAP[code];
  }

  private mapPassengers(
    travelers: FinnairPassengerItem[],
    currentSeatSelections: ServiceCatalogSelectionsForAllTravelers,
    seatMap: SeatmapSvgAndControlData
  ): SeatMapTraveler[] {
    const infants = travelers.filter((traveler) => traveler.passengerTypeCode === FinnairPassengerCode.INF);
    return travelers
      .filter((traveler) => traveler.passengerTypeCode !== FinnairPassengerCode.INF)
      .map((traveler) => {
        const regularSeatSelections = currentSeatSelections?.[this.flight().id]?.find(
          (p) => p.travelerId === traveler.id
        );
        const bundleSeatSelections = currentSeatSelections?.[this.flight().id]
          ?.filter((s) => s.category !== SubCategory.SEAT)
          .find((p) => p.travelerId === traveler.id);

        const selections = isPresent(bundleSeatSelections) ? bundleSeatSelections : regularSeatSelections;
        const seatNumber = selections?.seatNumber;
        const originalSeatNumber = servicesForFlightAndTravelerId(this.includedSeats, this.flight().id, traveler.id)[0]
          ?.seatNumber;
        const seat = seatNumber && seatMap.seats?.find((s) => s.seatNumber === seatNumber);
        const infant = infants.find((i) => i.accompanyingTravelerId === traveler.id);
        return {
          id: traveler.id,
          traveler,
          withInfant: !!infant,
          withInfantFullName: infant ? `${infant.firstName} ${infant.lastName}` : '',
          seat,
          seatNumber,
          originalSeatNumber,
        };
      });
  }

  private selectablePassengerIds(): string[] {
    return this.passengers()
      .filter((traveler) => traveler.passengerTypeCode !== FinnairPassengerCode.INF)
      .map((traveler) => traveler.id);
  }

  private getOperatingAirlineId(order: FinnairOrder, operatingAirlineCode: string): FinnairOperatingAirlineId {
    const operatingAirlineId = order.operatingAirlineIds.find(
      (operatingAirlineId) => operatingAirlineId.airlineCode === operatingAirlineCode
    );

    if (operatingAirlineId) {
      return operatingAirlineId;
    }

    const airlineName = order.bounds
      .flatMap((bound) => bound.itinerary.filter(isFlight))
      .find((flight) => flight.operatingAirline.code === operatingAirlineCode)?.operatingAirline.name;

    return airlineName && !isFinnairNorraOrWetLease(airlineName, operatingAirlineCode)
      ? {
          airlineCode: operatingAirlineCode,
          id: order.id,
          airlineName,
        }
      : undefined;
  }

  protected readonly Object = Object;
}
